import type { JSX } from 'react';

/**
 * #95: preview de template Meta con variables interpoladas.
 *
 * Renderiza el bodyText del template y reemplaza placeholders {{N}} con:
 *   - El valor mapeado en variableMap (key='1' → "Juan")
 *   - O `{{N}}` literal en color atenuado si no hay mapeo
 *
 * Muestra placeholders detectados con badge para que admin sepa cuáles
 * faltan mapear. Paridad legacy form Outbound META.
 */

interface MetaTemplatePreviewProps {
  bodyText: string;
  variableMap: Array<[string, string]>;
  /** Placeholders declarados en el template (de OutboundTemplate.placeholders). */
  declaredPlaceholders?: readonly string[];
}

function detectPlaceholders(body: string): string[] {
  const matches = body.match(/\{\{(\w+)\}\}/g) ?? [];
  const ids = matches.map((m) => m.slice(2, -2));
  return Array.from(new Set(ids));
}

function renderWithVars(body: string, map: Map<string, string>): JSX.Element {
  const parts = body.split(/(\{\{\w+\}\})/g);
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^\{\{(\w+)\}\}$/);
        if (!m) return <span key={i}>{p}</span>;
        const key = m[1]!;
        const val = map.get(key);
        if (val !== undefined && val !== '') {
          return <strong key={i} className="text-emerald-700">{val}</strong>;
        }
        return <span key={i} className="text-slate-400 italic">{p}</span>;
      })}
    </>
  );
}

export function MetaTemplatePreview({
  bodyText,
  variableMap,
  declaredPlaceholders,
}: MetaTemplatePreviewProps): JSX.Element {
  const map = new Map(variableMap);
  const detected = detectPlaceholders(bodyText);
  const placeholders = declaredPlaceholders && declaredPlaceholders.length > 0
    ? Array.from(new Set([...declaredPlaceholders, ...detected]))
    : detected;
  const unmapped = placeholders.filter((p) => !map.get(p));

  return (
    <div
      className="rounded border border-slate-200 bg-slate-50 p-3"
      data-testid="meta-template-preview"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Preview
        </span>
        {unmapped.length > 0 && (
          <span
            className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
            data-testid="meta-template-unmapped"
          >
            {unmapped.length} variable{unmapped.length === 1 ? '' : 's'} sin mapear: {unmapped.map((p) => `{{${p}}}`).join(', ')}
          </span>
        )}
      </div>
      <div className="whitespace-pre-wrap rounded bg-white p-3 text-sm text-slate-800 shadow-inner">
        {renderWithVars(bodyText, map)}
      </div>
      {placeholders.length > 0 && (
        <div className="mt-2 text-xs text-slate-500">
          Placeholders detectados: {placeholders.map((p) => (
            <code key={p} className="ml-1 rounded bg-slate-200 px-1 font-mono">{`{{${p}}}`}</code>
          ))}
        </div>
      )}
    </div>
  );
}
